import Vue from 'vue';

// Release cutover timestamp: October 1, 2026 00:00:00 EDT (UTC-4)
export const OCT1_CUTOVER_DATE = new Date('2026-10-01T00:00:00-04:00');
// Release campaign end timestamp: November 1, 2026 00:00:00 EDT (UTC-4)
export const NOV1_END_DATE = new Date('2026-11-01T00:00:00-04:00');

// Shared reactive state for release mode across components
export const releaseState = Vue.observable({
    mode: 'auto', // 'auto' | 'pre' | 'post'
    currentDate: new Date(),
    isOct1Active: false
});

// Event bus for direct release state change events
export const releaseBus = new Vue();

/**
 * Check if the current environment is production
 */
export function isProductionEnvironment() {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname.toLowerCase();
    return host === 'www.jornaypm-pro.com' || host === 'jornaypm-pro.com';
}

/**
 * Determine if Settings/Tuner should be accessible in production
 */
export function isSettingsAuthorized() {
    if (typeof window === 'undefined') return false;
    if (!isProductionEnvironment()) return true;
    
    // In production, allow access only if explicitly authorized via query param
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('settings') === '1' || 
           searchParams.get('tuner') === '1' || 
           searchParams.get('admin') === '1' ||
           searchParams.get('preview') === '1';
}

/**
 * Initialize release manager from URL search params, localStorage, or date
 */
export function initReleaseManager() {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    const releaseParam = searchParams.get('release');
    const oct1Param = searchParams.get('oct1');
    const storedMode = localStorage.getItem('jornay_hcp_release_mode');

    let mode = 'auto';

    // 1. URL search parameters take highest precedence
    if (releaseParam === 'pre' || oct1Param === '0') {
        mode = 'pre';
    } else if (releaseParam === 'post' || oct1Param === '1') {
        mode = 'post';
    } else if (releaseParam === 'auto') {
        mode = 'auto';
    } else if (storedMode && ['auto', 'pre', 'post'].includes(storedMode)) {
        // 2. Stored user selection from Settings drawer
        mode = storedMode;
    }

    setReleaseMode(mode, false);
}

/**
 * Update the active release mode and persist to state
 * @param {'auto'|'pre'|'post'} mode 
 * @param {boolean} persist 
 */
export function setReleaseMode(mode, persist = true) {
    releaseState.mode = mode;
    releaseState.currentDate = new Date();

    if (persist && typeof window !== 'undefined') {
        localStorage.setItem('jornay_hcp_release_mode', mode);
    }

    if (mode === 'post') {
        releaseState.isOct1Active = true;
    } else if (mode === 'pre') {
        releaseState.isOct1Active = false;
    } else {
        // Auto mode: active strictly during October (Oct 1 to Nov 1)
        const now = releaseState.currentDate.getTime();
        releaseState.isOct1Active = now >= OCT1_CUTOVER_DATE.getTime() && now < NOV1_END_DATE.getTime();
    }

    releaseBus.$emit('release-mode-changed', {
        mode: releaseState.mode,
        isOct1Active: releaseState.isOct1Active
    });
}

/**
 * Check if October 1st changes are currently active
 * @returns {boolean}
 */
export function isOct1Active() {
    if (releaseState.mode === 'post') return true;
    if (releaseState.mode === 'pre') return false;
    const now = new Date().getTime();
    return now >= OCT1_CUTOVER_DATE.getTime() && now < NOV1_END_DATE.getTime();
}

/**
 * Helper to compute days until/since October 1, 2026
 */
export function getCutoverStatus() {
    const now = new Date();
    const nowMs = now.getTime();
    const startMs = OCT1_CUTOVER_DATE.getTime();
    const endMs = NOV1_END_DATE.getTime();
    const diffStartMs = startMs - nowMs;
    const diffDays = Math.ceil(diffStartMs / (1000 * 60 * 60 * 24));
    const isPastEnd = nowMs >= endMs;
    const daysSinceEnd = Math.max(0, Math.ceil((nowMs - endMs) / (1000 * 60 * 60 * 24)));
    
    return {
        now,
        cutoverDate: OCT1_CUTOVER_DATE,
        endDate: NOV1_END_DATE,
        isPastCutover: diffStartMs <= 0,
        isActive: nowMs >= startMs && nowMs < endMs,
        isPastEnd,
        daysRemaining: Math.max(0, diffDays),
        daysSince: Math.max(0, -diffDays),
        daysSinceEnd
    };
}
